import type { ResolutionOption } from '../../../graphql/query/getResolutionOptions';
import { ImageCarousel } from '../../ImageCarousel/ImageCarousel';
import { Flex } from '@repo/ui/Flex';
import type { ResolutionData } from './ResolutionTaskSchema';
import { ItemCard } from '../../ItemCard/ItemCard';
import { getItemDescription } from '../../../utils/item';
import { getValidImageUrl } from '../../../utils/image';
import { useWorkflow } from '../../../workflow/hooks';
import s from './ResolutionTask.module.css';

export type ExchangePreviewProps = {
  itemId: string;
  resolutionData?: ResolutionOption;
  exchangeItem?: ResolutionData['resolution']['exchangeItem'];
};

export const ExchangePreview = (props: ExchangePreviewProps) => {
  const { resolutionData, exchangeItem, itemId } = props;
  const { order } = useWorkflow();

  const exchanges = resolutionData?.exchanges;

  const sameProductExchange = exchanges?.sameProduct;
  const sameProductExchangeAvailable =
    sameProductExchange &&
    sameProductExchange.variants.length > 0 &&
    sameProductExchange.attributeOptions.length > 0;

  if (!resolutionData || !resolutionData.exchanges) return null;

  // Get parent item's image as fallback
  const parentItem = order?.items?.find((item) => item.id === itemId);
  const parentItemImage = parentItem?.image?.url;

  const uniqueImages = Array.from(
    new Map(
      sameProductExchange?.variants
        .filter(({ image }) => image && image.trim() !== '')
        .map(({ image, name, variantId }) => [
          image,
          { id: variantId, name, url: image },
        ]),
    ).values(),
  );

  // Use parent item's image as fallback when no exchange images are available
  const fallbackImages =
    uniqueImages.length > 0
      ? uniqueImages
      : parentItemImage && parentItemImage.trim() !== ''
        ? [{ id: 'parent', name: parentItem?.name ?? '', url: parentItemImage }]
        : [];

  const selectedVariant = sameProductExchange?.variants.find(
    (v) =>
      v.productId === exchangeItem?.productId &&
      v.variantId === exchangeItem?.variantId,
  );
  const selectedVariantDescription =
    selectedVariant &&
    getItemDescription({
      item: {
        variant: {
          id: selectedVariant.variantId ?? '',
          info: [
            ...(selectedVariant.price?.formattedString
              ? [
                  {
                    name: 'price',
                    value: selectedVariant.price.formattedString,
                  },
                ]
              : []),
            ...selectedVariant.attributes.map((attr) => ({
              name: attr.name.toLowerCase(),
              value: attr.value,
            })),
          ],
        },
      },
    });

  return (
    <div>
      {sameProductExchangeAvailable && (
        <Flex padding="pt-24" gap="g-24" direction="column">
          {selectedVariant ? (
            <ItemCard
              className={s.itemCardWrapper}
              imageSize="sm"
              image={getValidImageUrl(selectedVariant.image, parentItemImage)}
              padding="p-8"
            >
              <Flex direction="column" justifyContent="space-evenly">
                <div className={s.itemDescription}>
                  {!sameProductExchange && (
                    <strong className={s.itemTitle}>
                      {selectedVariant.name}
                    </strong>
                  )}
                  {selectedVariantDescription}
                </div>
              </Flex>
            </ItemCard>
          ) : (
            <ImageCarousel images={fallbackImages} />
          )}
        </Flex>
      )}
    </div>
  );
};
